/**
 * sleeperRosters.js — Build projected 2026 starters from live Sleeper data
 *
 * buildLabStarters() fetches (or uses cached) Sleeper player data and returns
 * a depth-chart-style object keyed by team abbrev, suitable for seeding the
 * Projection Lab's TeamEditor with real 2026 depth-chart data.
 *
 * Slots returned per team: QB1, RB1, RB2, WR1, WR2, WR3, WR4, TE1, TE2
 * Each slot: { player, pos, team, sleeperId, years_exp, isRookie, college, depthOrder, adpRank, inPlayerHistory }
 */

import { getCachedRosters, fetchCurrentRosters } from "./rosterUpdate";
import { getAdpRank } from "../data/adpData";
import { findPlayerHist } from "./projlabProjection";

// Sleeper non-standard codes → Mock1 codes (truly weird Sleeper-specific abbrevs)
const TEAM_NORM = {
  SFO: "SF",  GBP: "GB",  NOS: "NO",  NEP: "NE",
  KCC: "KC",  LVR: "LV",  ARZ: "ARI", HST: "HOU",
  BLT: "BAL", CLV: "CLE", PHL: "PHI",
};

// Sleeper standard codes that differ from our convention
const SLEEPER_TO_MOCK1 = { JAC: "JAX", LA: "LAR" };

function normTeam(t) {
  if (!t) return null;
  const u = t.toUpperCase();
  const normed = TEAM_NORM[u] ?? u;
  return SLEEPER_TO_MOCK1[normed] ?? normed;
}

const SKILL_POSITIONS = new Set(["QB", "RB", "WR", "TE"]);

// Strip trailing generational suffixes for PLAYER_HISTORY cross-reference
function stripNameSuffix(name) {
  return name.replace(/\s+(Jr\.|Sr\.|II|III|IV)$/i, "").trim();
}

function inHistory(name) {
  return Boolean(findPlayerHist(name));
}

// Slots to fill per position
const POS_SLOTS = { QB: 1, RB: 2, WR: 4, TE: 2 };

/**
 * Fetches or reads cached Sleeper player data, filters to active rostered
 * skill-position players, and returns a depth-chart object:
 *
 *   {
 *     BUF: {
 *       QB1: { player, pos, team, years_exp, college, depthOrder, adpRank, inPlayerHistory },
 *       RB1: { … },
 *       RB2: { … },
 *       WR1–WR4, TE1–TE2
 *     },
 *     …
 *   }
 *
 * Sort priority within each team+position group:
 *   1. depth_chart_order (ascending, null last)
 *   2. ADP overall rank (ascending)
 *   3. search_rank (ascending)
 *
 * @param {{ forceRefresh?: boolean }} [opts]
 * @returns {Promise<Record<string, Record<string, object>>>}
 */
export async function buildLabStarters({ forceRefresh = false } = {}) {
  // Get player map: name → { team, pos } from cache or fresh fetch
  let playerMap;
  if (!forceRefresh) {
    const cached = getCachedRosters();
    if (cached) {
      playerMap = cached.data;
    }
  }
  if (!playerMap) {
    playerMap = await fetchCurrentRosters();
  }

  // We need the raw Sleeper objects (not just name→{team,pos}), so fetch again
  // if we only have the summarized cache. Re-fetch the full blob directly.
  const res = await fetch("https://api.sleeper.app/v1/players/nfl");
  if (!res.ok) throw new Error(`Sleeper fetch failed: ${res.status}`);
  const raw = await res.json();

  // Build per-team depth charts
  // teams[abbrev][pos] = [ sleeperPlayerObj, … ] sorted
  const teams = {};

  for (const p of Object.values(raw)) {
    if (!p.active) continue;
    if (!p.team) continue;

    const pos = p.fantasy_positions?.[0] ?? p.position;
    if (!pos || !SKILL_POSITIONS.has(pos)) continue;

    const team = normTeam(p.team);
    if (!team) continue;

    const name = p.full_name || [p.first_name, p.last_name].filter(Boolean).join(" ");
    if (!name) continue;

    if (!teams[team]) teams[team] = {};
    if (!teams[team][pos]) teams[team][pos] = [];

    teams[team][pos].push({
      name,
      pos,
      team,
      sleeperId:   p.player_id ?? null,
      years_exp:   p.years_exp ?? null,
      college:     p.college ?? null,
      depthOrder:  p.depth_chart_order ?? null,
      search_rank: p.search_rank ?? 9999999,
    });
  }

  // Sort each team+pos group, then assign slots
  const result = {};

  for (const [abbrev, posByGroup] of Object.entries(teams)) {
    result[abbrev] = {};

    for (const [pos, count] of Object.entries(POS_SLOTS)) {
      const group = posByGroup[pos] || [];

      group.sort((a, b) => {
        // 1. depth_chart_order (null sorts last)
        const da = a.depthOrder ?? 9999;
        const db = b.depthOrder ?? 9999;
        if (da !== db) return da - db;

        // 2. ADP rank
        const adpA = getAdpRank(a.name);
        const adpB = getAdpRank(b.name);
        if (adpA !== adpB) return adpA - adpB;

        // 3. Sleeper search_rank
        return a.search_rank - b.search_rank;
      });

      for (let i = 0; i < count; i++) {
        const slotKey = `${pos}${i + 1}`;
        const p = group[i];
        if (!p) {
          result[abbrev][slotKey] = null;
          continue;
        }
        result[abbrev][slotKey] = {
          player:          p.name,
          pos:             p.pos,
          team:            abbrev,
          sleeperId:       p.sleeperId,
          years_exp:       p.years_exp,
          isRookie:        p.years_exp !== null && p.years_exp <= 1,
          college:         p.college,
          depthOrder:      p.depthOrder,
          adpRank:         getAdpRank(p.name),
          inPlayerHistory: inHistory(p.name),
        };
      }
    }
  }

  return result;
}

/**
 * Compares two starter maps (STARTERS_2025 shape vs buildLabStarters output)
 * and returns per-team change summaries.
 *
 * Both maps are keyed by team abbrev with slots QB1, RB1, RB2, WR1–WR4, TE1–TE2.
 * STARTERS_2025 slots have shape { player, pos, … }; buildLabStarters slots match.
 *
 * Returns only teams that have at least one change.
 *
 * @param {Record<string, Record<string, {player:string, pos:string}|null>>} oldStarters
 * @param {Record<string, Record<string, {player:string, pos:string}|null>>} newStarters
 * @returns {Array<{ team:string, added:Array<{player:string,pos:string,slot:string}>,
 *                   removed:Array<{player:string,pos:string,slot:string}>,
 *                   changed:Array<{slot:string,pos:string,from:string,to:string}> }>}
 */
export function diffStarters(oldStarters, newStarters) {
  const allTeams = new Set([...Object.keys(oldStarters), ...Object.keys(newStarters)]);
  const results = [];

  for (const team of [...allTeams].sort()) {
    const oldTeam = oldStarters[team] || {};
    const newTeam = newStarters[team] || {};
    const allSlots = new Set([...Object.keys(oldTeam), ...Object.keys(newTeam)]);

    const added = [];
    const removed = [];
    const changed = [];

    for (const slot of allSlots) {
      const oldSlot = oldTeam[slot];
      const newSlot = newTeam[slot];
      const oldName = oldSlot?.player ?? null;
      const newName = newSlot?.player ?? null;
      const pos = oldSlot?.pos ?? newSlot?.pos ?? slot.replace(/\d+$/, "");

      // Treat suffix-only differences as equal (e.g. "Harold Fannin Jr." === "Harold Fannin")
      const oldBase = oldName ? stripNameSuffix(oldName) : null;
      const newBase = newName ? stripNameSuffix(newName) : null;

      if (!oldName && newName) {
        added.push({ player: newName, pos, slot });
      } else if (oldName && !newName) {
        removed.push({ player: oldName, pos, slot });
      } else if (oldName && newName && oldBase !== newBase) {
        changed.push({ slot, pos, from: oldName, to: newName });
      }
    }

    if (added.length || removed.length || changed.length) {
      results.push({ team, added, removed, changed });
    }
  }

  return results;
}
